import type { KeyboardEvent, MouseEvent } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { GLOSSARY, type GlossaryTerm } from "@/lib/glossary";

const LABEL: Record<GlossaryTerm, string> = {
  seed: "seed", trawl: "trawl", snowball: "snowball", spotTrawl: "Spot Trawl",
  livingLit: "Living Lit", triage: "triage", markedRelevant: "marked relevant",
  researchQuestion: "research question", searchTerms: "search terms",
};

function stop(e: MouseEvent) {
  e.stopPropagation();
}

function stopActivationKeys(e: KeyboardEvent) {
  // Enter/Space on the hint must not activate a surrounding toggle/sort header.
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    e.stopPropagation();
  }
}

/** ⓘ glossary hint. Renders a span (not a button element) so it can sit inside
 *  clickable headers (e.g. the Seed Papers collapse toggle) without nesting buttons.
 *  Carries its own TooltipProvider so it works in isolated renders (tests, portals). */
export function TermHint({ term }: { term: GlossaryTerm }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="button"
            tabIndex={0}
            aria-label={`What is ${LABEL[term]}?`}
            aria-description={GLOSSARY[term]}
            onClick={stop}
            onKeyDown={stopActivationKeys}
            className="inline-flex cursor-help items-center rounded-sm text-muted-foreground/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <Info className="size-3" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{GLOSSARY[term]}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
